import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {useForm} from "react-hook-form";

import css from './SemiFinishedAddSemiFinishedModal.module.css';
import {
    semiFinishedActions,
    semiFinishedSemiFinishedActions
} from "../../../../store/slices/semiFinished-slice";
import { SearchableSelect } from "../../../../ui";


const SemiFinishedAddSemiFinishedModal = ({setSemiFinishedSemiFinishedAddOpen}) => {
    const [selectedSemiFinished, setSelectedSemiFinished] = useState("");
    const dispatch = useDispatch();
    const {t, i18n} = useTranslation();
    const currentLanguage = i18n.language;

    const {semiFinishedArray, trigger} = useSelector(store => store.semiFinished);
    const {semiFinishedSemiFinishedForModalArray} = useSelector(store => store.semiFinishedSemiFinished);

    const {
        register,
        handleSubmit,
        formState: {errors}
    } = useForm();

    useEffect(() => {
        dispatch(semiFinishedActions.getAllSemiFinished());
    }, [trigger, dispatch]);

    const handleAddSemiFinishedSubmit = (data) => {
        const selectedItem = semiFinishedArray.find(obj => obj.id === selectedSemiFinished);
        if (!selectedItem) return;

        const weightNetto = Number(data.weightNetto);

        // Auto-calculate weight based on weightNetto and semi-finished spec
        const originalWeight = parseFloat(selectedItem.weight) || 0;
        const originalWeightNetto = parseFloat(selectedItem.weightNetto) || 0;
        const nettoRatio = originalWeight > 0 ? originalWeightNetto / originalWeight : 1;
        const weight = nettoRatio > 0 ? Number((weightNetto / nettoRatio).toFixed(2)) : weightNetto;

        // Semi-finished items store total nutrients and cost for their entire weight
        const originalItemWeight = parseFloat(selectedItem.weight) || 100;
        const portionMultiplier = weight / originalItemWeight;

        const totalProductsCost = (selectedItem.products || []).reduce(
            (sum, p) => sum + (parseFloat(p.cost) || 0), 0
        );
        const totalChildSfCost = (selectedItem.includedSemiFinisheds || []).reduce(
            (sum, s) => sum + (parseFloat(s.cost) || 0), 0
        );
        const totalSemiFinishedCost = totalProductsCost + totalChildSfCost;

        const singleSemiFinished = {
            id: selectedItem.id,
            name: selectedItem.name,
            weight,
            weightNetto,
            cost: Number((totalSemiFinishedCost * portionMultiplier).toFixed(2)),
            energy: Number(((selectedItem.energy || 0) * portionMultiplier).toFixed(2)),
            fats: Number(((selectedItem.fats || 0) * portionMultiplier).toFixed(2)),
            proteins: Number(((selectedItem.proteins || 0) * portionMultiplier).toFixed(2)),
            carbohydrates: Number(((selectedItem.carbs || 0) * portionMultiplier).toFixed(2)),
        };

        dispatch(semiFinishedSemiFinishedActions.addSemiFinishedForModal(singleSemiFinished));
        dispatch(semiFinishedSemiFinishedActions.changeTrigger());
        setSemiFinishedSemiFinishedAddOpen(false);
    };

    // Prepare options for SearchableSelect
    const semiFinishedOptions = semiFinishedArray.map(semiFinished => ({
        id: semiFinished.id,
        name: semiFinished.name,
        label: semiFinished.name[currentLanguage] || semiFinished.name['en']
    }));

    return (
        <section className={css.overlay}>
            <div className={css.modal}>
                <button className={css.closeButton} onClick={() => setSemiFinishedSemiFinishedAddOpen(false)}>
                    &times;
                </button>
                <h2 className={css.title}>Add Semi-Finished</h2>

                <form className={css.content} onSubmit={handleSubmit(handleAddSemiFinishedSubmit)}>
                    <div className={css.inputGroup}>
                        <label htmlFor="group">Semi-Finished</label>
                        <SearchableSelect
                            options={semiFinishedOptions}
                            value={selectedSemiFinished}
                            onChange={setSelectedSemiFinished}
                            placeholder={t('select-semi-finished')}
                            searchPlaceholder={t('search-semi-finished')}
                            getOptionLabel={(option) => option.label}
                            getOptionValue={(option) => option.id}
                            isOptionDisabled={(option) => 
                                semiFinishedSemiFinishedForModalArray.some(p => 
                                    p.id === option.id || p.semiFinishedId === option.id
                                )
                            }
                            className={css.searchableSelect}
                        />
                    </div>

                    <div className={css.inputGroup}>
                        <label htmlFor="weightNetto">{t('WeightNetto')}</label>
                        <input
                            type="number"
                            id="weightNetto"
                            {...register("weightNetto", {required: true})}
                            className={errors.weightNetto ? css.inputError : ""}
                        />
                        {errors.weightNetto && <p className={css.error}>{errors.weightNetto.message}</p>}
                    </div>

                    <div className={css.actions}>
                        <button type="button" className={css.cancelButton}
                                onClick={() => setSemiFinishedSemiFinishedAddOpen(false)}>
                            Cancel
                        </button>
                        <button type="submit" className={css.okButton}>OK</button>
                    </div>
                </form>
            </div>
        </section>
    );
};

export {SemiFinishedAddSemiFinishedModal};
